import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RequestService } from '../shared/services/request-service';
import { Observable } from 'rxjs';
import { CompanyInstruction, CompanyInstructionGrouped, CompanyInstructionGroupedQuery, CompanyInstructionQuery, CompanyInstructionRevision, CompanyInstructionRevisionGrouped, CompanyInstructionType } from '../shared/dto/company-instruction';
import { clone } from '../settings-v2/shared/utils/clone';
import { htmlToText } from '../shared/pipes/html-to-text.pipe';
import { map } from 'rxjs/operators';
import { arrayLen } from '../shared/utils/array-len';

@Injectable({
  providedIn: 'root'
})
export class CompanyInstructionService {

  constructor(
    private http: HttpClient,
    private requestService: RequestService,
  ) { }

  public saveInstruction(companyId: number, instruction: CompanyInstruction): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/instructions`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.post<CompanyInstruction>(url, this.cleanInstructionData(instruction), { headers });
  }

  public updateInstruction(companyId: number, instruction: CompanyInstruction): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/instructions/${instruction.id}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.put<CompanyInstruction>(url, this.cleanInstructionData(instruction), { headers });
  }

  public getInstructionRevisions(companyId: number, instructionId: number): Observable<CompanyInstructionRevision[]> {
    const url = `/api/company/${companyId}/instructions/${instructionId}/revisions`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.get<CompanyInstructionRevision[]>(url, { headers });
  }

  public getRemovedInstructionsForCompany(companyId: number): Observable<CompanyInstructionRevisionGrouped> {
    const url = `/api/company/${companyId}/instructions/removed`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.get<CompanyInstructionRevisionGrouped>(url, { headers });
  }

  // Start of Team Instruction API
  public createTeamInstruction(companyId: number, groupId: number, instruction: CompanyInstruction): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/group/${groupId}/instructions`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.post<CompanyInstruction>(url, this.cleanInstructionData(instruction), { headers });
  }

  public updateTeamInstruction(id: number, companyId: number, groupId: number, instruction: CompanyInstruction): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/group/${groupId}/instructions/${id}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.put<CompanyInstruction>(url, this.cleanInstructionData(instruction), { headers });
  }

  public deleteTeamInstruction(id: number, companyId: number, groupId: number): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/group/${groupId}/instructions/${id}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.delete<CompanyInstruction>(url, { headers });
  }

  public getTeamInstructionRevisions(
    companyId: number,
    groupId: number,
    instructionId: number
  ): Observable<CompanyInstructionRevision[]> {
    const url = `/api/company/${companyId}/group/${groupId}/instructions/${instructionId}/revisions`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.get<CompanyInstructionRevision[]>(url, { headers });
  }
  // End of Team Instruction API

  public deleteInstruction(id: number, companyId: number): Observable<CompanyInstruction> {
    const url = `/api/company/${companyId}/instructions/${id}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.http.delete<CompanyInstruction>(url, { headers });
  }

  public searchCompanyInstruction(instructionSearch: CompanyInstructionQuery): Observable<CompanyInstruction[]> {
    const url = '/api/company/instructions/search';
    const headers = this.requestService.buildHttpHeaders();
    return this.http.post<CompanyInstruction[]>(url, instructionSearch, { headers });
  }

  public searchCompanyInstructionGrouped(
    instructionGrouped: CompanyInstructionGroupedQuery
  ): Observable<CompanyInstructionGrouped> {
    const url = '/api/company/instructions/grouped';
    const headers = this.requestService.buildHttpHeaders();
    return this.http.post<CompanyInstructionGrouped>(url, instructionGrouped, { headers });
  }

  public getInstructionByType(
    companyId: number,
    companyInstructionType: CompanyInstructionType,
  ): Observable<CompanyInstruction> {
    const instructionSearch: CompanyInstructionQuery = {
      companyId: companyId,
      instructionType: companyInstructionType,
    };
    return this.searchCompanyInstruction(instructionSearch)
      .pipe(
        map((instructions: CompanyInstruction[]) => {
          if (arrayLen(instructions)) {
            return instructions[0];
          }

          return null;
        })
      );
  }

  public getInstructionFromGroupedByType(
    instructionGrouped: CompanyInstructionGroupedQuery,
    companyInstructionType: CompanyInstructionType,
  ): Observable<CompanyInstruction> {
    return this.searchCompanyInstructionGrouped(instructionGrouped)
      .pipe(
        map((grouped: CompanyInstructionGrouped) => {
          if (!grouped || !grouped.clientCareInstructions) {
            return null;
          }

          return grouped
            .clientCareInstructions
            .find(instruction => instruction.instructionType === companyInstructionType);
        })
      );
  }

  public searchCompanyInstructionByType(companyId: number, instructionType: CompanyInstructionType): Observable<CompanyInstruction> {
    const instructionSearch: CompanyInstructionQuery = {
      companyId: companyId,
      instructionType: instructionType
    };
    return this.searchCompanyInstruction(instructionSearch)
      .pipe(
        map((instructions: CompanyInstruction[]) => {
          if (arrayLen(instructions)) {
            return instructions[0];
          }

          return null;
        })
      );
  }

  private cleanInstructionData(instruction: CompanyInstruction): CompanyInstruction {
    const data: CompanyInstruction = clone(instruction);

    // As advised, return an empty string if the stripped value is empty
    const plainInstructionText = htmlToText(instruction.instructionText);
    if (plainInstructionText.length === 0) {
      data.instructionText = '';
    }

    return data;
  }
}
